<template>
  <div class="flex masonry-openended-container" id="masonry-container">
    <div
      v-if="slideActive.type === 'openEnded' && !isLoaded"
      class="horizontal layout center center-justified full-size"
    >
      <div class="text-center">
        <div class="quiz--result">
          {{ $t("loadingResult") }}...
        </div>
        <Loading class="mx-auto my-0" />
      </div>
    </div>

    <div
      v-if="slideActive.type === 'ideas' && slideActive.SlideOptions.length === 0"
      class="no-ideas"
      :class="{'white': isTextColourLight}"
    >
      {{ $t('noSubmissions') }}.
    </div>
    <div
      v-masonry
      :transition-duration="`${masonryTransitionDuration}ms`"
      item-selector=".item"
      class="masonry-content"
      v-if="masonryVisible"
    >
      <div
        v-masonry-tile
        v-for="(item, index) in options"
        :key="item.id"
        class="item"
      >
        <div
          class="body-content"
          :class="[{'has-audience-info': item.audienceName
                      || item.audienceTeam || item.audienceEmoji || item.audienceEmail ||
                      slideActive.type === 'ideas',
                    'has-ideas-rank': isVotingStepResult,},
                   _setClassAnimationTada(item),
                   _setClassAnimationDelay(item)]"
          :style="{ 'box-shadow': setBoxShadowValue(item, index),
                    'background-color': getOpenEndedBackground()}"
          @click="$emit('pickOption', item, index)"
        >
          <div v-if="item.audience">
            <ButtonStartVoice
              class="button-voice"
              :audienceUnique="item.audience"
              :audienceName="item.audienceName"
            />
          </div>
          <div
            class="ideas-rank-badge animate__animated animate__fadeIn animate__delay-1s"
            :style="{'background-color': setToColorPalette(item, index)}"
            :class="{'small': item.rankOrder && item.rankOrder > 99}"
            v-if="isVotingStepResult"
          >
            <span>{{ item.rankOrder || 1 }}</span>
          </div>
          <div
            class="content-title"
          >
            <ui-pre-line
              :text="item.title"
              class="flex-1"
              :class="{'hide-submission': isBlurAnswerContent,
                       'has-transition': slideActive.type === 'ideas'}"
            />
            <ButtonEnlargeImage
              :image="item.image"
              class="content-image"
              :class="{'mt-0': !item.title && item.image}"
              v-show="item.image && isImageSubmissionVisible"
              :noContent="!item.title && !slideActive.imageSubmission"
            />
            <aha-icon
              name="image"
              class="text-secondary-20"
              v-if="!slideActive.imageSubmission && !item.title"
            />
          </div>
          <div
            class="audience-info"
            :class="{'dark': isTextColourLight,
                     'has-vote-count-content': slideActive.type === 'ideas'}"
            v-show="_checkShowAudienceInfo(item)"
          >
            <div class="truncate">
              <div class="truncate">
                <span
                  class="info--emoji"
                  v-if="_checkShowAudienceFields('avatar', item.audienceEmoji)"
                >
                  {{ item.audienceEmoji }}
                </span>
                <span
                  v-if="_checkShowAudienceFields('name', item.audienceName)"
                  class="text-base font-bold"
                >
                  <profanity-filter :text="item.audienceName" />
                </span>
              </div>
              <div>
                <p
                  class="info--other-team truncate"
                  v-if="_checkShowAudienceFields('team', item.audienceTeam)"
                >
                  <profanity-filter :text="item.audienceTeam" />
                </p>
                <p
                  class="info--other-email truncate"
                  v-if="_checkShowAudienceFields('email', item.audienceEmail)"
                >
                  <profanity-filter :text="item.audienceEmail" />
                </p>
              </div>
            </div>
            <div
              class="info--vote-count"
              :class="{'opacity-0': isVotingStepSubmission}"
              v-if="slideActive.type === 'ideas'"
            >
              <aha-icon-container
                size="xs"
                icon-class="bg-base-0 text-base-90 vote-count-container container-custom-size"
              >
                <aha-icon
                  name="like"
                  class="text-base-50 mb-0.5"
                  width="16"
                  height="16"
                  viewBox="0 0 24 24"
                />
                <span
                  class="pl-1 text-sm font-bold text-base-90"
                  v-show="isVoteCountTextVisible"
                >{{ (item.votesCount || 0) | formatTotalPoint }}</span>
              </aha-icon-container>
            </div>
          </div>
          <span
            class="ml-2 mr-1 remove-icon"
            @click.stop="$emit('removeResponse', item, index)"
            v-if="removeResponseButtonVisible"
            :class="{'alway-show': item.deleting}"
          >
            <b-button v-tooltip="$i18nPresenter.t('Remove this response')" class="tooltip-btn" />
            <i class="fas fa-times-circle" v-if="!item.deleting" />
            <span
              v-else
              class="loading-spinner"
              :class="{'black': !isTextColourLight}"
            />
          </span>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { mapState, mapGetters } from 'vuex'
import PreLine from '@/components/UI/PreLine.vue'
import ProfanityFilter from '@/components-v2/common/utils/ProfanityFilter.vue'
import Loading from '@/components/Loading'
import constants from '@/constant'
import Util from '@/util'
import EventBus from '@/eventBus'
import ButtonEnlargeImage from '@/components-v2/presentation-editor/middle/ButtonEnlargeImage.vue'
import ButtonStartVoice from '@/components-v2/voice-audio/ButtonStartVoice.vue'

export default {
  components: {
    ProfanityFilter,
    UiPreLine: PreLine,
    Loading,
    ButtonEnlargeImage,
    ButtonStartVoice,
  },
  props: {
    isLoaded: {
      type: Boolean,
      default: false,
    },
    options: {
      type: [Array, Object],
      required: true,
    },
    masonryTransitionDuration: {
      type: Number,
      default: null,
    },
  },
  data() {
    return {
      tabName: constants.IdeasTabName,
      firstRankAnimation: false,
      firework: null,
    }
  },
  computed: {
    ...mapState({
      slideActive: (state) => state.slideActive,
      sharePresentation: (state) => state.sharePresentation,
    }),
    ...mapGetters(['isBackstage', 'isTextColourLight']),
    ...mapGetters('slideModule', ['modalBackgroundColour']),
    masonryVisible() {
      return this.slideActive.type === 'openEnded' ||
      (this.slideActive.type === 'ideas' && this.slideActive.SlideOptions.length !== 0)
    },
    isVotingStepSubmission() {
      return this.slideActive.type === 'ideas' && this.slideActive.votingStep === this.tabName.SUBMISSION
    },
    isVotingStepVoting() {
      return this.slideActive.type === 'ideas' && this.slideActive.votingStep === this.tabName.VOTING
    },
    isVotingStepResult() {
      return this.slideActive.type === 'ideas' && this.slideActive.votingStep === this.tabName.RESULT
    },
    removeResponseButtonVisible() {
      return ((!this.sharePresentation && this.slideActive.type === 'openEnded') ||
        (!this.sharePresentation && this.slideActive.type === 'ideas' &&
          this.slideActive.votingStep === this.tabName.SUBMISSION)) && !this.isBackstage
    },
    isBlurAnswerContent() {
      return !this.slideActive.showSubmissions && this.isVotingStepSubmission
    },
    isVoteCountTextVisible() {
      return (this.isVotingStepVoting && this.slideActive.showVotes) ||
        this.isVotingStepResult || this.isVotingStepSubmission
    },
    isImageSubmissionVisible() {
      return this.slideActive.imageSubmission && this.slideActive.type === 'openEnded'
    },
  },
  watch: {
    '$store.state.presentation.fontFamily': function watchFontFamily() {
      this.redrawMasonry()
    },
    '$store.state.presentation.filteringProfanity': function watchFilteringProfanity() {
      this.redrawMasonry()
    },
    '$store.state.slideActive.votingStep': function watchVotingStep() {
      if (this.isVotingStepResult) {
        this._handleShowFirstRankAnimation()
      } else {
        this.firstRankAnimation = false
        if (this.firework) {
          this.firework.stopFirework()
          this.firework = null
        }
      }
    },
  },
  mounted() {
    this.redrawMasonry()
    EventBus.$on('on-reDraw-masonry', () => {
      this.redrawMasonry()
    })
    if (this.isVotingStepResult) {
      this._handleShowFirstRankAnimation()
    }
  },
  destroyed() {
    EventBus.$off('on-reDraw-masonry')
    if (this.firework) this.firework.stopFirework()
    Util.allowPDFRender()
  },
  methods: {
    _handleShowFirstRankAnimation() {
      Util.disallowPDFRender()
      setTimeout(() => {
        this.firstRankAnimation = true
        setTimeout(() => {
          this.firework = Util.firework()
        }, 4500)
        setTimeout(() => {
          if (this.firework) this.firework.stopFirework()
        }, 8000)
        setTimeout(() => {
          Util.allowPDFRender()
        }, 9000)
      }, 1300)
    },
    _checkShowAudienceFields(fieldName, audienceInfo) {
      return Util.checkShowAudienceFields(fieldName, audienceInfo)
    },
    _checkShowAudienceInfo(answer) {
      return Util.checkShowAudienceInfo(answer)
    },
    setToColorPalette(item, index) {
      return Util.setToColorPalette(item, index)
    },
    setBoxShadowValue(item, index) {
      return `0 0 0 1.5px ${Util.setToColorPalette(item, index)}`
    },
    getOpenEndedBackground() {
      const color = Util.hexToRgba(this.modalBackgroundColour)
      return Util.changeRgbaColor(color, 0.6)
    },
    redrawMasonry() {
      const DELAY = 900
      Util.disallowPDFRender()
      setTimeout(() => {
        this.$redrawVueMasonry()
        if (this.slideActive.type === 'openEnded') {
          setTimeout(() => {
            Util.allowPDFRender()
          }, this.masonryTransitionDuration + 3000)
        }
      }, DELAY)
    },
    _hasRankOrder(item) {
      return item.votesCount && item.rankOrder &&
        this.firstRankAnimation &&
        this.isVotingStepResult
    },
    _isTopThreeRankOrder(item) {
      return item.rankOrder < 4
    },
    _isFirstRankOrder(item) {
      return item.rankOrder === 1
    },
    _isSecondRankOrder(item) {
      return item.rankOrder === 2
    },
    _isThirdRankOrder(item) {
      return item.rankOrder === 3
    },
    _setClassAnimationTada(item) {
      if (this._hasRankOrder(item) && this._isTopThreeRankOrder(item)) {
        return 'animate__animated animate__tada animate__repeat-3'
      }
      return ''
    },
    _setClassAnimationDelay(item) {
      if (this._hasRankOrder(item) && this._isFirstRankOrder(item)) {
        return 'animate__delay-1s'
      }
      if (this._hasRankOrder(item) && this._isSecondRankOrder(item)) {
        return 'animate__delay-2s'
      }
      if (this._hasRankOrder(item) && this._isThirdRankOrder(item)) {
        return 'animate__delay-3s'
      }
      return ''
    },
  },
}
</script>

<style lang="scss" scoped>
@import "@/assets/scss/variable.scss";
@import "@/assets/scss-v2/variables.scss";

.button-voice {
  position: absolute;
  bottom: 4px;
  right: 22px;
  display: none;

  &::v-deep {
    .button-video-call {
      padding: 2px 8px;
      border-radius: 4px;
      font-size: 12px;
    }
  }
}

.masonry-content {
  @apply w-full h-full;
}
.no-ideas {
  font-weight: bold;
  color: #707070;
  padding-top: 16px;
  font-size: 20px;
  &.white {
    color: #ffffff;
    opacity: 0.8;
  }
}
.masonry-openended-container {
  width: 100%;
  height: 100%;
  overflow-x: hidden;
  overflow-y: auto;
  -ms-overflow-style: auto;
  -webkit-overflow-scrolling: touch;
  position: relative;
  scrollbar-width: none;
  &::-webkit-scrollbar {
    display: none;
  }

  .item {
    padding: 14px 11px 7px 11px;
    width: calc(100% / 3);
    z-index: 9;

    .body-content {
      border-radius: 8px;
      transition: all 0.25s;
      cursor: pointer;
      word-break: break-word;
      position: relative;
      padding: 10px 16px;
      &.has-ideas-rank {
        overflow: hidden;
        position: relative;
        .ideas-rank-badge {
          display: block;
          background: #bad3f1;
          position: absolute;
          top: -24px;
          right: -32px;
          width: 65px;
          height: 55px;
          text-align: center;
          font-size: 16px;
          font-weight: bold;
          color: #fff;
          transform: rotate(45deg);
          text-transform: uppercase;
          background-color: #596681;
          line-height: 88px;
          &.small {
            font-size: 14px;
          }
        }
        .content-title {
          padding-right: 35px;
        }
      }
      &:hover {
        transform: scale(1.05);
        .remove-icon {
          opacity: 0.5;
          &:hover {
            opacity: 1;
          }
        }
        .button-voice {
          display: block;
        }
      }
    }
    &:hover {
      z-index: 20;
    }
  }
}

.has-audience-info {
  .info--other-team, .info--other-email {
    font-size: 0.85em;
  }
  &.body-content {
    padding: 0!important;
    .content-title {
      padding: 12px 16px;
      .has-transition {
        transition: all 0.2s ease;
      }
      .hide-submission {
        filter: blur(4px);
        opacity: 0.2;
      }
    }
    .audience-info {
      padding: 7px 16px;
      line-height: 1.2;
      &.has-vote-count-content {
        display: flex;
        align-items: center;
        min-height: 40px;
      }
      .info--emoji {
        min-width: 20px;
        font-size: 15px;
        margin-top: 2px;
        display: inline-block;
      }
      .info--vote-count {
        @apply flex text-right flex-1 justify-end;
        .vote-count-container {
          &.container-custom-size {
            height: 1.7rem;
          }
          @apply w-max h-8 px-2 border border-base-50;
          margin-right: 0 !important; // overwrite bootstrap
        }
      }
    }
  }
  &.open-ended-title {
    padding: 0 !important;
    -webkit-overflow-scrolling: touch; //Chrome
    -ms-overflow-style: none; //IE, edge
    overflow: -moz-scrollbars-none; //Firefox
    scrollbar-width: none;
    &::-webkit-scrollbar {
      display: none;
    }
    .title-text {
      padding: 0 30px 25px;
      max-height: 150px;
      min-height: 75px;
      overflow-y: auto;
      margin-top: 25px;
      -ms-overflow-style: auto;
    }
    .audience-info {
      padding: 12px 30px;
      line-height: 1.2;
      .info--emoji {
        min-width: 63px;
        font-size: 45px;
      }
    }
  }
  &.open-ended-picked {
    padding: 0 !important;
    overflow-y: hidden;
    .picked-title {
      padding: 0 30px 25px;
      margin-top: 25px;
      max-height: 202px;
      min-height: 81px;
      overflow-y: auto;
      -ms-overflow-style: auto;
    }
    .audience-info {
      padding: 12px 30px;
      line-height: 1.2;
      .info--emoji {
        min-width: 65px;
        font-size: 48px;
      }
    }
  }
}
.audience-info {
  background-color: rgba(240,241,241,1);
  color: inherit;
  border-radius: 0 0 8px 8px;
  &.dark {
    background-color: rgba(240,241,241,0.1);
  }
}
.remove-icon {
  position: absolute;
  top: 0;
  right: 0;
  margin-top: 0.2rem;
  opacity: 0;
  height: 20px;
  .fa-times-circle {
    color: $question-tooltips-color;
  }
  .tooltip-btn {
    position: absolute;
    padding: 0.55rem;
    opacity: 0;
    top: 1.5px;
  }

  &.alway-show {
    opacity: 1;
  }
}
.content-image {
  @apply mt-2;
  &::v-deep .enlarge-image__image {
    max-height: 92px;
    max-width: 100%;
  }
}
</style>
